import React, { useState, useEffect } from 'react';
import MDEditor from '@uiw/react-md-editor';
import { DataLayer } from '../lib/dataLayer';
import { Database, LanguageLevel } from '../types/database';
import { useAuth } from '../contexts/AuthContext';

type Lesson = Database['public']['Tables']['lessons']['Row'];
type Card = Database['public']['Tables']['cards']['Row'];

interface LessonEditorProps {
  lesson?: Lesson | null;
  onSave: (lesson: Lesson) => void;
  onCancel: () => void;
}

export default function LessonEditor({ lesson, onSave, onCancel }: LessonEditorProps) {
  const { user } = useAuth();
  const [title, setTitle] = useState(lesson?.title || '');
  const [slug, setSlug] = useState(lesson?.slug || '');
  const [description, setDescription] = useState(lesson?.description || '');
  const [content, setContent] = useState(lesson?.content || '');
  const [language, setLanguage] = useState(lesson?.language || 'es');
  const [level, setLevel] = useState<LanguageLevel | ''>(lesson?.level || '');
  const [tags, setTags] = useState(lesson?.tags?.join(', ') || '');
  const [exercise, setExercise] = useState(lesson?.exercise || '');
  const [orderIndex, setOrderIndex] = useState(lesson?.order_index || 0);
  const [isPublished, setIsPublished] = useState(lesson?.is_published || false);
  const [saving, setSaving] = useState(false);
  const [availableCards, setAvailableCards] = useState<Card[]>([]);
  const [selectedCards, setSelectedCards] = useState<string[]>([]);
  const [loadingCards, setLoadingCards] = useState(true);

  useEffect(() => {
    loadAvailableCards();
    if (lesson) {
      loadLessonCards();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lesson?.id]);

  const loadAvailableCards = async () => {
    try {
      const cards = await DataLayer.getCards();
      setAvailableCards(cards);
    } catch (error) {
      console.error('Failed to load cards:', error);
    } finally {
      setLoadingCards(false);
    }
  };

  const loadLessonCards = async () => {
    if (!lesson?.id) return;
    try {
      const cards = await DataLayer.getLessonCards(lesson.id);
      setSelectedCards(cards.map(c => c.id));
    } catch (error) {
      console.error('Failed to load lesson cards:', error);
    }
  };

  const generateSlug = (text: string) => {
    return text
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  };

  const handleTitleChange = (value: string) => {
    setTitle(value);
    // Auto-generate slug for new lessons
    if (!lesson && value) {
      setSlug(generateSlug(value));
    }
  };

  const handleSave = async () => {
    if (!user || !title || !slug || !content) {
      alert('Please fill in all required fields');
      return;
    }

    setSaving(true);
    try {
      const lessonData = {
        title,
        slug,
        description: description || null,
        content,
        language,
        level: level || null,
        tags: tags ? tags.split(',').map(t => t.trim()).filter(Boolean) : [],
        exercise: exercise || null,
        order_index: orderIndex,
        is_published: isPublished
      };

      let savedLesson: Lesson;
      if (lesson) {
        // Update existing lesson
        await DataLayer.updateLesson(lesson.id, lessonData, user.id);
        savedLesson = { ...lesson, ...lessonData, updated_at: new Date().toISOString(), last_edited_by: user.id };
      } else {
        // Create new lesson
        savedLesson = await DataLayer.createLesson(lessonData, user.id);
      }

      // Update lesson cards
      if (lesson) {
        // Remove all existing cards
        const existingCards = await DataLayer.getLessonCards(lesson.id);
        for (const card of existingCards) {
          await DataLayer.removeCardFromLesson(lesson.id, card.id);
        }
      }

      // Add selected cards
      for (let i = 0; i < selectedCards.length; i++) {
        await DataLayer.addCardToLesson(savedLesson.id, selectedCards[i], i);
      }

      onSave(savedLesson);
    } catch (error) {
      console.error('Failed to save lesson:', error);
      alert('Failed to save lesson. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const levels: LanguageLevel[] = ['A1', 'A2', 'B1', 'B2', 'C1', 'C2'];

  const filteredCards = availableCards.filter(card => {
    if (exercise && card.tags && card.tags.includes(exercise)) {
      return true;
    }
    if (level && card.level === level) {
      return true;
    }
    if (tags) {
      const tagList = tags.split(',').map(t => t.trim());
      return card.tags?.some(t => tagList.includes(t));
    }
    return false;
  });

  return (
    <div className="max-w-6xl mx-auto">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-900">
          {lesson ? 'Edit Lesson' : 'Create New Lesson'}
        </h2>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Title and slug */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Title <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={title}
              onChange={(e) => handleTitleChange(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Lesson title"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Slug <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={slug}
              onChange={(e) => setSlug(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="lesson-slug"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Description
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              rows={2}
              placeholder="Brief description of the lesson"
            />
          </div>

          {/* Content editor */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Content <span className="text-red-500">*</span>
            </label>
            <MDEditor
              value={content}
              onChange={(val) => setContent(val || '')}
              preview="edit"
              height={500}
            />
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Metadata */}
          <div className="bg-white p-6 rounded-lg border border-gray-200">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Lesson Settings</h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Language
                </label>
                <select
                  value={language}
                  onChange={(e) => setLanguage(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="es">Spanish</option>
                  <option value="en">English</option>
                  <option value="fr">French</option>
                  <option value="de">German</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Level
                </label>
                <select
                  value={level}
                  onChange={(e) => setLevel(e.target.value as LanguageLevel | '')}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">No specific level</option>
                  {levels.map(l => (
                    <option key={l} value={l}>{l}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Tags (comma separated)
                </label>
                <input
                  type="text"
                  value={tags}
                  onChange={(e) => setTags(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="grammar, verbs, pronouns"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Exercise Tag
                </label>
                <input
                  type="text"
                  value={exercise}
                  onChange={(e) => setExercise(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="pronombres"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Order Index
                </label>
                <input
                  type="number"
                  value={orderIndex}
                  onChange={(e) => setOrderIndex(parseInt(e.target.value) || 0)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="isPublished"
                  checked={isPublished}
                  onChange={(e) => setIsPublished(e.target.checked)}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <label htmlFor="isPublished" className="ml-2 block text-sm text-gray-900">
                  Published
                </label>
              </div>
            </div>
          </div>

          {/* Related Cards */}
          <div className="bg-white p-6 rounded-lg border border-gray-200">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Related Cards</h3>
            
            {loadingCards ? (
              <div className="text-sm text-gray-500">Loading cards...</div>
            ) : (
              <>
                <div className="text-sm text-gray-600 mb-2">
                  {filteredCards.length} cards match this lesson's criteria
                </div>
                <div className="max-h-64 overflow-y-auto space-y-2">
                  {filteredCards.map(card => (
                    <label key={card.id} className="flex items-start">
                      <input
                        type="checkbox"
                        checked={selectedCards.includes(card.id)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedCards([...selectedCards, card.id]);
                          } else {
                            setSelectedCards(selectedCards.filter(id => id !== card.id));
                          }
                        }}
                        className="mt-1 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                      <div className="ml-2 text-sm">
                        <div className="font-medium text-gray-900">{card.front}</div>
                        <div className="text-gray-500">{card.back}</div>
                      </div>
                    </label>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Actions */}
      <div className="mt-6 flex justify-end gap-3">
        <button
          onClick={onCancel}
          className="px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors"
        >
          Cancel
        </button>
        <button
          onClick={handleSave}
          disabled={saving || !title || !slug || !content}
          className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {saving ? 'Saving...' : 'Save Lesson'}
        </button>
      </div>
    </div>
  );
}